import type { Key } from "react";

/**
 * Достаёт ключи выбранных значений из value селекта.
 * Поддерживает single/multiple, сырые значения (string | number)
 * и формат labelInValue ({ value, label }).
 */
export const getSelectedKeys = (value: unknown): Set<Key> => {
  const keys = new Set<Key>();

  const add = (item: unknown) => {
    if (item == null) return;
    if (typeof item === "object") {
      const inner = (item as { value?: unknown }).value;
      if (typeof inner === "string" || typeof inner === "number") {
        keys.add(inner);
      }
      return;
    }
    if (typeof item === "string" || typeof item === "number") {
      keys.add(item);
    }
  };

  if (Array.isArray(value)) {
    value.forEach(add);
  } else {
    add(value);
  }

  return keys;
};

/**
 * Убирает дубли опций по `value` (оставляет первое вхождение),
 * рекурсивно для групп с вложенным `options`.
 * Дубли value ломают rc-select: он использует value как React-ключ
 * виртуального списка, и при переупорядочивании (наш hoist)
 * строки получают чужое состояние — клики и выделение «съезжают».
 */
export const dedupeOptionsByValue = <T extends { value?: Key; options?: T[] }>(
  options: T[],
): T[] => {
  const seen = new Set<Key>();

  return options
    .filter((option) => {
      if (option.value == null) return true;
      if (seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    })
    .map((option) =>
      Array.isArray(option.options)
        ? ({ ...option, options: dedupeOptionsByValue(option.options) } as T)
        : option,
    );
};

/**
 * Стабильно поднимает выбранные опции в начало списка.
 * Относительный порядок внутри групп «выбранные» / «остальные» сохраняется.
 * Для группированных опций (antd OptGroup — опция с вложенным `options`)
 * hoist применяется рекурсивно внутри каждой группы.
 */
export const sortOptionsSelectedFirst = <
  T extends { value?: Key; options?: T[] },
>(
  options: T[],
  selectedKeys: Set<Key>,
): T[] => {
  if (selectedKeys.size === 0 || options.length === 0) {
    return options;
  }

  const selected: T[] = [];
  const rest: T[] = [];

  options.forEach((option) => {
    const next = Array.isArray(option.options)
      ? ({
          ...option,
          options: sortOptionsSelectedFirst(option.options, selectedKeys),
        } as T)
      : option;

    if (option.value != null && selectedKeys.has(option.value)) {
      selected.push(next);
    } else {
      rest.push(next);
    }
  });

  return [...selected, ...rest];
};


export const flattenOptions = <T extends { value?: Key; options?: T[] }>(
  options: T[],
): T[] => {
  const flat: T[] = [];

  options.forEach((option) => {
    if (Array.isArray(option.options)) {
      flat.push(...flattenOptions(option.options));
    } else {
      flat.push(option);
    }
  });

  return flat;
};


export const getOptionFromLabelInValue = <T>(
  value: unknown,
  key: Key,
): T | undefined => {
  const items = Array.isArray(value) ? value : [value];
  const found = items.find(
    (item) =>
      item != null &&
      typeof item === "object" &&
      (item as { value?: unknown }).value === key,
  );

  if (found && typeof found === "object") {
    const { label } = found as { label?: unknown };
    if (label != null) {
      return { value: key, label } as T;
    }
  }

  return undefined;
};

/**
 * Для легаси-селектов на react-select (Fields/*): поднимает выбранные
 * опции в начало списка и добавляет выбранные опции, которых нет в
 * загруженных страницах (выбраны через серверный поиск и выпали после
 * сброса пагинации). react-select хранит опции { value, label } прямо
 * в value, поэтому недостающие берутся из самого значения.
 */
export const mergeReactSelectSelectedFirst = <T extends { value?: Key }>(
  options: T[],
  value: unknown,
): T[] => {
  const selected = (
    Array.isArray(value) ? value : value != null ? [value] : []
  ).filter((item): item is T => item != null && typeof item === "object");

  if (selected.length === 0 || options.length === 0) {
    return options;
  }

  const selectedKeys = new Set<Key>();
  selected.forEach((item) => {
    if (item.value != null) selectedKeys.add(item.value);
  });

  if (selectedKeys.size === 0) {
    return options;
  }

  const loadedKeys = new Set(options.map((option) => option?.value));
  const missing = selected.filter(
    (item) => item.value != null && !loadedKeys.has(item.value),
  );
  const pinned = options.filter(
    (option) => option?.value != null && selectedKeys.has(option.value),
  );
  const rest = options.filter(
    (option) => option?.value == null || !selectedKeys.has(option.value),
  );

  return [...missing, ...pinned, ...rest];
};

/**
 * Компаратор для antd `filterSort`: сохраняет порядок «выбранные вверху»
 * при локальном поиске (antd применяет filterSort к отфильтрованным опциям).
 * Возврат 0 — исходный порядок сохраняется (Array.sort стабилен).
 */
export const filterSortSelectedFirst =
  (selectedKeys: Set<Key>) =>
  (
    optionA: { value?: Key | null },
    optionB: { value?: Key | null },
  ): number => {
    const aSelected = optionA.value != null && selectedKeys.has(optionA.value);
    const bSelected = optionB.value != null && selectedKeys.has(optionB.value);

    if (aSelected !== bSelected) {
      return aSelected ? -1 : 1;
    }
    return 0;
  };
